import { useRef, useState } from 'react';
import { operatorConsole, type PackBuilderPack } from '../../lib/api/operatorConsole';
import { previewLaunchUrl } from '../../lib/previewLaunchUrl';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';

/** Remembered like the Theme Editor's player id — a preview needs a real
 *  player of this operator and nobody wants to retype it per pack. */
export const PREVIEW_PLAYER_ID_KEY = 'operator_console.pack_builder.preview_player_id';

function readStoredPlayerId(): string {
  try {
    return localStorage.getItem(PREVIEW_PLAYER_ID_KEY) ?? '';
  } catch {
    return '';
  }
}

function storePlayerId(id: string): void {
  try {
    localStorage.setItem(PREVIEW_PLAYER_ID_KEY, id);
  } catch {
    // Storage unavailable (private mode) — the preview still works.
  }
}

interface Props {
  pack: PackBuilderPack;
  open: boolean;
  onClose: () => void;
}

export function PreviewModal({ pack, open, onClose }: Props) {
  const [playerId, setPlayerId] = useState(readStoredPlayerId);
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A ref, not `loading`: a held Enter fires several keydowns before React has
  // re-rendered with the new state, and each one would start its own POST.
  const inFlight = useRef(false);

  async function handleLoad() {
    const id = playerId.trim();
    if (!id || inFlight.current) return;
    inFlight.current = true;
    try {
      setLoading(true);
      setError(null);
      storePlayerId(id);
      // preview_staged session: the iframe lists this pack even while it is
      // unpublished; buying stays impossible until it is enabled.
      const res = await operatorConsole.packBuilder.previewUrl(pack.id, { player_id: id });
      // The backend returns the session pieces; this build knows the origin.
      // A missing origin throws and lands in the error surface below.
      setLaunchUrl(previewLaunchUrl(res));
    } catch (err) {
      setLaunchUrl(null);
      setError(err instanceof Error ? err.message : 'Could not start the preview');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }

  return (
    <Modal open={open} title={`Preview: ${pack.title}`} onClose={onClose} size="wide">
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-[11px] font-medium text-[var(--co-muted)]">
            Player id
            <Input
              aria-label="Player id"
              value={playerId}
              onChange={(e) => setPlayerId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleLoad();
              }}
              placeholder="required"
              className="w-56"
            />
          </label>
          <Button
            onClick={() => void handleLoad()}
            disabled={loading || playerId.trim() === ''}
            className="px-3 py-1.5 text-[12px]"
          >
            {loading ? 'Loading…' : 'Load preview'}
          </Button>
          {launchUrl && (
            <a
              href={launchUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[12px] text-[var(--co-muted)] underline-offset-2 hover:underline"
            >
              Open in a new tab
            </a>
          )}
        </div>
        <p className="text-[11px] text-[var(--co-muted)]">
          The preview opens a real session for this player id. Unpublished packs are visible;
          purchases stay disabled until you publish.
        </p>
        {error && (
          <p role="alert" className="text-[12px] text-[var(--co-error)]">
            {error}
          </p>
        )}
        {launchUrl && (
          <iframe
            title="Pack preview"
            src={launchUrl}
            className="h-[70vh] w-full rounded-lg border border-[var(--co-border)] bg-[var(--co-bg)]"
          />
        )}
      </div>
    </Modal>
  );
}
